import {
  ApplyForRefundConfirmation,
  ApplyForRefundIneligible,
  ApplyForRefundPage,
} from '@ovotech/energy-cx';
import {
  GlobalErrorPage,
  KapiFeature,
  PageWithNavigationAndSectionPadding,
  BillingTypeRouteGuard,
} from '@/src/components';
import { PayxHoldingPage } from '@/src/components/PayxHoldingPage';
import { PAYGTopupPage } from '@/src/pages/PAYGTopup/PAYGTopupPage';
import { PAYGTopupHistoryPage } from '@/src/pages/PAYGTopupHistory';
import { PAYGTopupDetailPage } from '@/src/pages/PAYGTopupDetail';
import {
  ONE_CLICK_PAYMENT_REDUCTION,
  SHOW_RECOMMENDED_DIRECT_DEBIT,
} from '@/src/constants/feature-flags';
import {
  EXTERNAL_ROUTE_HELP,
  OLD_ROUTE_CHANGE_DD_AMOUNT,
  OLD_ROUTE_CHANGE_DD_DATE,
  ROUTE_ADJUST_DD_AMOUNT_CALCULATION,
  ROUTE_BILLING_DETAILS,
  ROUTE_BILLING_HISTORY,
  ROUTE_BOILER_COVER,
  ROUTE_CHANGE_DD_AMOUNT,
  ROUTE_CHANGE_DD_AMOUNT_CONFIRM,
  ROUTE_CHANGE_DD_DATE,
  ROUTE_CHANGE_DD_DATE_CONFIRM,
  ROUTE_DD_CALCULATOR,
  ROUTE_ENERGY_INSIGHTS,
  ROUTE_GLOBAL_ERROR,
  ROUTE_GREENER_ENERGY,
  ROUTE_GREENER_ENERGY_SIGNUP,
  ROUTE_GREENER_ENERGY_SIGNUP_SUCCESS,
  ROUTE_GREENER_ELECTRICITY,
  ROUTE_GREENER_ELECTRICITY_SIGNUP,
  ROUTE_GREENER_ELECTRICITY_SIGNUP_SUCCESS,
  ROUTE_HELP,
  ROUTE_HOLDING,
  ROUTE_HOME,
  ROUTE_MAKE_CARD_PAYMENT,
  ROUTE_METER_READINGS,
  ROUTE_MOVING_HOME,
  ROUTE_MY_THERMOSTAT,
  ROUTE_OFFERS,
  ROUTE_ONE_CLICK_PAYMENT_REDUCTION_CONFIRMATION,
  ROUTE_OPENING_METER_READINGS,
  ROUTE_PAYG_TOPUP,
  ROUTE_PAYG_TOPUP_HISTORY,
  ROUTE_PAYG_TOPUP_DETAIL,
  ROUTE_PAYMENTS,
  ROUTE_PLAN,
  ROUTE_POWER_MOVE,
  ROUTE_PROFILE,
  ROUTE_RECOMMENDED_DD,
  ROUTE_REFUNDS_CONFIRM,
  ROUTE_REFUNDS_NOT_ELIGIBLE,
  ROUTE_REFUNDS_ONE_STOP,
  ROUTE_RENEWAL,
  ROUTE_RENEWAL_SVT,
  ROUTE_RENEWAL_SVT_COMM,
  ROUTE_SELECT_ACCOUNT,
  ROUTE_SETUP_DD,
  ROUTE_SMART_THERMOSTATS_OFFER,
  ROUTE_USAGE,
  ROUTE_ANYTIME,
  ROUTE_REWARDS,
  ROUTE_HOME_EXPLICIT,
  ROUTE_BILLS,
  ROUTE_EDIT_PROFILE_MARKETING,
  ROUTE_SETTINGS,
  ROUTE_GET_APP,
  ROUTE_SMART_BOOKING,
  ROUTE_PAYX,
} from '@/src/constants/routes';
import { BoilerCover } from '@/src/pages/BoilerCover';
import { EnergyInsightsPages } from '@/src/pages/EnergyInsights';
import { OffersPage } from '@/src/pages/Offers/OffersPage';
import { AdjustDirectDebit } from '@/src/pages/Payments/DirectDebitCalculator/AdjustDirectDebit/AdjustDirectDebit';
import OneClickPaymentReductionConfirmation from '@/src/pages/Payments/OneClickPaymentReductionConfirmation';
import { SelectAccount } from '@/src/pages/SelectAccount';
import { OptimizelyFeature } from '@/src/utils/optimizely';
import { getDeviceOS } from '@/src/utils/getDeviceOS';
import { utmParamsOnly } from '@/src/utils/utmParamsOnly';
import React, { ComponentType } from 'react';
import { Redirect, RouteComponentProps, withRouter } from 'react-router-dom-v5';
import {
  BillingDetailsPage,
  BillingHistory,
  ChangeDirectDebitAmount,
  ChangeDirectDebitAmountConfirm,
  ChangeDirectDebitDate,
  ChangeDirectDebitDateConfirm,
  DirectDebitCalculator,
  GreenerEnergy,
  GreenerEnergySignupPage,
  GreenerEnergySignupSuccess,
  GreenerElectricity,
  GreenerElectricitySignupPage,
  GreenerElectricitySignupSuccess,
  Holding,
  Home,
  MeterReadings,
  MovingHome,
  OpeningMeterReadings,
  Payments,
  Plan,
  Profile,
  RecommendedDirectDebit,
  Renewal,
  SetupDirectDebit,
  Usage,
  AppLandingPage,
  SmartBookingRedirect,
} from '../pages';
import ChargeAnytimeV2 from '@/src/pages/AnytimeEligibilityV2/ChargeAnytime';
import { CardPaymentSessionRoutes } from '@/src/pages/Payments/CardPaymentSession';
import { URL_CONTENT_IN_APP } from '@/src/pages/Shop/Shop';
import { ActionCardsPreviewScreen } from '../pages/preview/ActionCardsPreview';
import { AllRewards } from '../pages/AllRewards/AllRewards';
import { AccountBillingType } from '../hooks/useAccountBillingType';

const OneClickPaymentReductionFeature = () => (
  <OptimizelyFeature feature={ONE_CLICK_PAYMENT_REDUCTION}>
    {isEnabled =>
      isEnabled ? (
        <OneClickPaymentReductionConfirmation />
      ) : (
        <Redirect to={ROUTE_HOME} />
      )
    }
  </OptimizelyFeature>
);

type RouteType = {
  path: string;
  component: ComponentType<any>;
  exact?: boolean;
  allowedBillingType?: Exclude<AccountBillingType, 'PAYX'>;
};

const routesConfig: Array<RouteType> = [
  {
    path: ROUTE_HOME,
    component: () => (
      <KapiFeature>
        <Home />
      </KapiFeature>
    ),
    exact: true,
  },
  {
    path: ROUTE_HOME_EXPLICIT,
    component: () => <Redirect to={ROUTE_HOME} />,
    exact: true,
  },
  {
    path: ROUTE_PROFILE,
    component: Profile,
  },
  {
    path: '/your-energy',
    component: () => <Redirect to={ROUTE_HOME} />,
    exact: true,
  },
  {
    path: '/your-profile',
    component: () => <Redirect to={ROUTE_PROFILE} />,
    exact: true,
  },
  {
    path: ROUTE_SETTINGS,
    component: withRouter(({ location }: RouteComponentProps) => (
      <Redirect
        to={{
          pathname:
            location.search == '?type=marketing-preferences'
              ? ROUTE_EDIT_PROFILE_MARKETING
              : '/profile',
        }}
      />
    )) as any,
    exact: true,
  },
  {
    path: ROUTE_SELECT_ACCOUNT,
    component: SelectAccount,
    exact: true,
  },
  {
    path: ROUTE_PAYX,
    component: PayxHoldingPage,
    exact: true,
  },
  {
    path: `${ROUTE_PAYG_TOPUP}/:msn`,
    component: () => (
      <BillingTypeRouteGuard allow="PAYG">
        <PAYGTopupPage />
      </BillingTypeRouteGuard>
    ),
    exact: true,
  },
  {
    path: ROUTE_PAYG_TOPUP_HISTORY,
    component: PAYGTopupHistoryPage,
    exact: true,
    allowedBillingType: 'PAYG',
  },
  {
    path: ROUTE_PAYG_TOPUP_DETAIL,
    component: PAYGTopupDetailPage,
    exact: true,
    allowedBillingType: 'PAYG',
  },
  {
    path: ROUTE_GLOBAL_ERROR,
    component: GlobalErrorPage,
    exact: true,
  },
  {
    path: ROUTE_HELP,
    component: () => {
      // Currently just pointing to prod, may need to point to UAT in future?
      window.location.replace(EXTERNAL_ROUTE_HELP);
      return null;
    },
    exact: true,
  },
  {
    path: ROUTE_PLAN,
    component: () => (
      <KapiFeature>
        <Plan />
      </KapiFeature>
    ),
    exact: true,
  },
  {
    path: ROUTE_HOLDING,
    component: Holding,
    exact: true,
  },
  {
    path: '/greener-energy-signup',
    component: withRouter(({ location }: RouteComponentProps) => (
      <Redirect
        to={{
          pathname: ROUTE_GREENER_ENERGY_SIGNUP,
          search: location.search,
        }}
      />
    )) as any,
    exact: true,
  },
  {
    path: ROUTE_GREENER_ENERGY,
    component: GreenerEnergy,
    exact: true,
  },
  {
    path: ROUTE_GREENER_ENERGY_SIGNUP,
    component: GreenerEnergySignupPage,
    exact: true,
  },
  {
    path: ROUTE_GREENER_ENERGY_SIGNUP_SUCCESS,
    component: GreenerEnergySignupSuccess,
    exact: true,
  },
  {
    path: ROUTE_GREENER_ELECTRICITY,
    component: GreenerElectricity,
    exact: true,
  },
  {
    path: ROUTE_GREENER_ELECTRICITY_SIGNUP,
    component: GreenerElectricitySignupPage,
    exact: true,
  },
  {
    path: ROUTE_GREENER_ELECTRICITY_SIGNUP_SUCCESS,
    component: GreenerElectricitySignupSuccess,
    exact: true,
  },
  {
    path: ROUTE_PAYMENTS,
    component: Payments,
    exact: true,
    allowedBillingType: 'PAYM',
  },
  {
    path: ROUTE_ADJUST_DD_AMOUNT_CALCULATION,
    component: ChangeDirectDebitAmount,
    exact: true,
    allowedBillingType: 'PAYM',
  },
  {
    path: ROUTE_ONE_CLICK_PAYMENT_REDUCTION_CONFIRMATION,
    component: OneClickPaymentReductionFeature,
    exact: true,
    allowedBillingType: 'PAYM',
  },
  {
    path: ROUTE_CHANGE_DD_AMOUNT,
    component: () => (
      <OptimizelyFeature feature={SHOW_RECOMMENDED_DIRECT_DEBIT}>
        {isEnabled =>
          isEnabled ? <AdjustDirectDebit /> : <ChangeDirectDebitAmount />
        }
      </OptimizelyFeature>
    ),
    exact: true,
    allowedBillingType: 'PAYM',
  },

  {
    path: OLD_ROUTE_CHANGE_DD_AMOUNT,
    component: () => (
      <OptimizelyFeature feature={SHOW_RECOMMENDED_DIRECT_DEBIT}>
        {isEnabled =>
          isEnabled ? <DirectDebitCalculator /> : <ChangeDirectDebitAmount />
        }
      </OptimizelyFeature>
    ),
    exact: true,
    allowedBillingType: 'PAYM',
  },
  {
    path: ROUTE_CHANGE_DD_AMOUNT_CONFIRM,
    component: ChangeDirectDebitAmountConfirm,
    exact: true,
    allowedBillingType: 'PAYM',
  },
  {
    path: '/preview/action-cards',
    component:
      process.env.VERCEL_ENV === 'preview'
        ? ActionCardsPreviewScreen
        : () => <Redirect to={ROUTE_HOME} />,
    exact: true,
    allowedBillingType: 'PAYM',
  },
  {
    path: OLD_ROUTE_CHANGE_DD_DATE,
    component: () => <Redirect to={ROUTE_CHANGE_DD_DATE} />,
    exact: true,
    allowedBillingType: 'PAYM',
  },
  {
    path: ROUTE_CHANGE_DD_DATE,
    component: ChangeDirectDebitDate,
    exact: true,
    allowedBillingType: 'PAYM',
  },
  {
    path: ROUTE_CHANGE_DD_DATE_CONFIRM,
    component: ChangeDirectDebitDateConfirm,
    exact: true,
    allowedBillingType: 'PAYM',
  },
  {
    path: ROUTE_DD_CALCULATOR,
    component: () => (
      <OptimizelyFeature feature={SHOW_RECOMMENDED_DIRECT_DEBIT}>
        {isEnabled =>
          isEnabled ? <DirectDebitCalculator /> : <ChangeDirectDebitAmount />
        }
      </OptimizelyFeature>
    ),
    exact: true,
    allowedBillingType: 'PAYM',
  },
  {
    path: ROUTE_RECOMMENDED_DD,
    component: () => (
      <OptimizelyFeature feature={SHOW_RECOMMENDED_DIRECT_DEBIT}>
        {isEnabled =>
          isEnabled ? <RecommendedDirectDebit /> : <Redirect to={ROUTE_HOME} />
        }
      </OptimizelyFeature>
    ),
    exact: true,
    allowedBillingType: 'PAYM',
  },
  {
    path: ROUTE_MAKE_CARD_PAYMENT,
    component: () => <CardPaymentSessionRoutes />,
    allowedBillingType: 'PAYM',
  },
  {
    path: ROUTE_REFUNDS_ONE_STOP,
    component: () => (
      <PageWithNavigationAndSectionPadding title="Apply for your refund">
        <KapiFeature>
          <ApplyForRefundPage />
        </KapiFeature>
      </PageWithNavigationAndSectionPadding>
    ),
    exact: true,
    allowedBillingType: 'PAYM',
  },
  {
    path: ROUTE_REFUNDS_CONFIRM,
    component: () => (
      <PageWithNavigationAndSectionPadding title="Confirmation - Apply for a refund">
        <ApplyForRefundConfirmation />
      </PageWithNavigationAndSectionPadding>
    ),
    exact: true,
    allowedBillingType: 'PAYM',
  },
  {
    path: ROUTE_REFUNDS_NOT_ELIGIBLE,
    component: () => (
      <PageWithNavigationAndSectionPadding title="Not eligible - Apply for a refund">
        <ApplyForRefundIneligible />
      </PageWithNavigationAndSectionPadding>
    ),
    exact: true,
    allowedBillingType: 'PAYM',
  },
  {
    path: ROUTE_USAGE,
    component: Usage,
  },
  {
    path: '/charges',
    component: () => <Redirect to={ROUTE_BILLING_HISTORY} />,
    exact: true,
    allowedBillingType: 'PAYM',
  },
  {
    path: '/charges/:year/:month',
    component: withRouter(
      ({
        match: {
          params: { year, month },
        },
      }: RouteComponentProps<{ year: string; month: string }>) => (
        <Redirect to={`${ROUTE_BILLING_DETAILS}/${year}/${month}`} />
      ),
    ),
    exact: true,
    allowedBillingType: 'PAYM',
  },
  {
    path: '/billing/:year/:month',
    component: withRouter(
      ({
        match: {
          params: { year, month },
        },
      }: RouteComponentProps<{ year: string; month: string }>) => (
        <Redirect to={`${ROUTE_BILLING_DETAILS}/${year}/${month}`} />
      ),
    ),
    exact: true,
    allowedBillingType: 'PAYM',
  },
  {
    path: ROUTE_BILLING_DETAILS,
    component: () => (
      <KapiFeature>
        <BillingDetailsPage />
      </KapiFeature>
    ),
    allowedBillingType: 'PAYM',
  },
  {
    path: '/billing',
    component: () => <Redirect to={ROUTE_BILLING_DETAILS} />,
    exact: true,
    allowedBillingType: 'PAYM',
  },
  {
    path: `/billing/:index(\\d+)`,
    component: withRouter(props => {
      const { index } = props.match.params;

      return <Redirect to={`${ROUTE_BILLING_DETAILS}/${index}`} />;
    }),
    allowedBillingType: 'PAYM',
  },
  {
    path: ROUTE_BILLING_HISTORY,
    component: () => (
      <KapiFeature>
        <BillingHistory />
      </KapiFeature>
    ),
    allowedBillingType: 'PAYM',
  },
  {
    path: ROUTE_BILLS,
    component: () => <Redirect to={ROUTE_BILLING_HISTORY} />,
    exact: true,
    allowedBillingType: 'PAYM',
  },
  {
    path: ROUTE_MOVING_HOME,
    component: () => <MovingHome />,
    allowedBillingType: 'PAYM',
  },
  {
    path: ROUTE_SETUP_DD,
    component: SetupDirectDebit,
    allowedBillingType: 'PAYM',
  },
  {
    path: ROUTE_RENEWAL_SVT_COMM,
    component: () => <Redirect to={ROUTE_RENEWAL_SVT} />,
  },
  {
    path: ROUTE_RENEWAL,
    component: Renewal,
  },
  {
    path: ROUTE_METER_READINGS,
    component: () => (
      <KapiFeature>
        <MeterReadings />
      </KapiFeature>
    ),
  },
  {
    path: ROUTE_OPENING_METER_READINGS,
    component: OpeningMeterReadings,
  },
  {
    path: '/zero-in',
    component: EnergyInsightsPages.Hub,
  },
  {
    path: ROUTE_MY_THERMOSTAT,
    component: () => <Redirect to={ROUTE_USAGE} />,
  },
  {
    path: ROUTE_BOILER_COVER,
    component: BoilerCover,
  },
  {
    path: ROUTE_OFFERS,
    component: OffersPage,
    exact: true,
    allowedBillingType: 'PAYM',
  },
  {
    path: ROUTE_SMART_THERMOSTATS_OFFER,
    component: () => <Redirect to={ROUTE_OFFERS} />,
    allowedBillingType: 'PAYM',
  },
  {
    path: ROUTE_ANYTIME,
    component: ChargeAnytimeV2,
    allowedBillingType: 'PAYM',
  },
  {
    path: ROUTE_POWER_MOVE,
    component: () => <Redirect to={ROUTE_REWARDS.ALL_REWARDS} />,
  },
  {
    path: ROUTE_ENERGY_INSIGHTS.CARBON_INTENSITY,
    component: EnergyInsightsPages.CarbonIntensity,
  },
  {
    path: ROUTE_ENERGY_INSIGHTS.BILL_BREAKDOWN,
    component: EnergyInsightsPages.BillBreakdown,
  },
  {
    path: ROUTE_ENERGY_INSIGHTS.HUB,
    component: EnergyInsightsPages.Hub,
  },
  {
    path: ROUTE_ENERGY_INSIGHTS.TERMS,
    component: EnergyInsightsPages.Terms,
  },
  {
    path: ROUTE_REWARDS.ALL_REWARDS,
    component: AllRewards,
  },
];

export const routes = routesConfig.map(route => {
  const allowedBillingType = route.allowedBillingType;

  return {
    ...route,
    component: allowedBillingType
      ? () => (
          <BillingTypeRouteGuard allow={allowedBillingType}>
            <route.component />
          </BillingTypeRouteGuard>
        )
      : route.component,
  };
});

export const publicRoutes: Array<RouteType> = [
  {
    path: '/shop',
    component: () => {
      window.location.replace(URL_CONTENT_IN_APP);
      return <div data-testid="shop-redirect" />;
    },
    exact: true,
  },
  {
    path: '/free-elec/signup',
    component: () => {
      window.location.replace(URL_CONTENT_IN_APP);
      return <div data-testid="free-elec-signup-redirect" />;
    },
    exact: true,
  },
  {
    path: '/free-elec/dashboard',
    component: () => {
      window.location.replace(URL_CONTENT_IN_APP);
      return <div data-testid="free-elec-dashboard-redirect" />;
    },
    exact: true,
  },
  {
    path: '/free-heat/signup',
    component: () => {
      window.location.replace(URL_CONTENT_IN_APP);
      return <div data-testid="free-heat-signup-redirect" />;
    },
    exact: true,
  },
  {
    path: '/free-heat/dashboard',
    component: () => {
      window.location.replace(URL_CONTENT_IN_APP);
      return <div data-testid="free-heat-dashboard-redirect" />;
    },
    exact: true,
  },
  {
    path: '/power-move/progress',
    component: () => {
      window.location.replace(URL_CONTENT_IN_APP);
      return <div data-testid="power-move-progress-redirect" />;
    },
    exact: true,
  },
  {
    path: '/power-move/signup',
    component: () => {
      window.location.replace(URL_CONTENT_IN_APP);
      return <div data-testid="power-move-signup-redirect" />;
    },
    exact: true,
  },
  {
    path: '/power-move/join',
    component: () => {
      window.location.replace(URL_CONTENT_IN_APP);
      return <div data-testid="power-move-join-redirect" />;
    },
    exact: true,
  },
  {
    path: '/rewards',
    component: () => {
      window.location.replace(
        getDeviceOS() !== null
          ? `${ROUTE_GET_APP}${utmParamsOnly(window.location.search)}`
          : ROUTE_REWARDS.ALL_REWARDS,
      );
      return <div data-testid="rewards-redirect" />;
    },
    exact: true,
  },
  {
    path: '/ovo-live',
    component: () => {
      window.location.replace('https://www.ovo-live.com/');
      return <div data-testid="ovo-live-redirect" />;
    },
    exact: true,
  },
  {
    path: ROUTE_GET_APP,
    component: AppLandingPage,
    exact: true,
  },
  {
    path: ROUTE_SMART_BOOKING,
    component: SmartBookingRedirect,
    exact: true,
  },
];
